import axios from 'axios';
import { store } from '../redux/store';
import config from '../config/config';

// Base configuration
const BASE_URL = config.server.url.replace(/\/+$/, ''); // Remove trailing slashes
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Create and configure Axios instance
 */
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

/**
 * Request Interceptor
 * - Adds authentication token
 * - Handles API path prefixing
 * - Logs requests in development
 */
api.interceptors.request.use(
  (config) => {
    const modifiedConfig = { ...config };
    
    // Add /apis prefix to API routes if not present
    if (modifiedConfig.url && 
        !modifiedConfig.url.startsWith('http') && 
        !modifiedConfig.url.startsWith('/apis/')) {
      modifiedConfig.url = `/apis${modifiedConfig.url.startsWith('/') ? '' : '/'}${modifiedConfig.url}`;
    }
    
    // Add auth token if available
    const token = store.getState()?.user?.token;
    if (token) {
      modifiedConfig.headers.Authorization = `Bearer ${token}`;
    }

    return modifiedConfig;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor
 * - Handles errors consistently
 * - Logs responses in development
 */
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

/**
 * Format error response consistently
 */
const isRequestCancelled = (error) =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.message === 'canceled';

const formatError = (error) => {
  if (isRequestCancelled(error)) {
    return {
      success: false,
      cancelled: true,
      message: 'Request cancelled',
    };
  }
  if (error.response) {
    // Server responded with error status
    return {
      success: false,
      status: error.response.status,
      message: error.response.data?.message || 'Request failed',
      data: error.response.data
    };
  } else if (error.request) {
    // No response received
    return {
      success: false,
      message: 'No response from server. Please check your connection.',
      isNetworkError: true
    };
  } else {
    // Request setup error
    return {
      success: false,
      message: error.message || 'Request setup failed'
    };
  }
};

/**
 * GET request. Supports optional axios options (e.g. signal for abort).
 * Usage: export const getFooApi = async (params, options = {}) => getRequest(PATH, params, options);
 */
export const getRequest = async (path, params = {}, options = {}) => {
  try {
    const response = await api.get(path, { params, ...options });
    
    // For binary responses (PDF, images, etc.), return the raw data
    if (options.responseType === 'arraybuffer' || options.responseType === 'blob') {
      return response.data;
    }
    
    return response.data;
  } catch (error) {
    return formatError(error);
  }
};

/**
 * POST request
 * @param {string} path - API endpoint
 * @param {object} data - Request payload
 * @param {object} options - Additional Axios config options
 * @returns {Promise<object>}
 */
export const postRequest = async (path, data, options = {}) => {
  try {
    const response = await api.post(path, data, options);
    return response.data;
  } catch (error) {
    return formatError(error);
  }
};

/**
 * PUT request
 * @param {string} path - API endpoint
 * @param {object} data - Request payload
 * @param {object} options - Additional Axios config options
 * @returns {Promise<object>}
 */
export const putRequest = async (path, data, options = {}) => {
  try {
    const response = await api.put(path, data, options);
    return response.data;
  } catch (error) {
    return formatError(error);
  }
};

/**
 * DELETE request
 * @param {string} path - API endpoint
 * @param {object} options - Additional Axios config options
 * @returns {Promise<object>}
 */
export const deleteRequest = async (path, options = {}) => {
  try {
    const response = await api.delete(path, options);
    return response.data;
  } catch (error) {
    return formatError(error);
  }
};

/**
 * File upload helper
 * @param {string} path - API endpoint
 * @param {File} file - File to upload
 * @param {object} additionalData - Additional form data
 * @returns {Promise<object>}
 */
export const uploadFile = async (path, file, additionalData = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  // Append additional data if provided
  Object.entries(additionalData).forEach(([key, value]) => {
    formData.append(key, value);
  });
  
  return postRequest(path, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
